import { useAccessCode } from '@rhg/hooks/use-access-code';
import useAppConfig from '@rhg/hooks/use-app-config';
import { usePublicBrandingPartnerConfig } from '@rhg/hooks/use-public-branding-partner-config';
import commonMessages from '@rhg/lang/messages/common';
import { logger } from '@rhg/libs/logger';
import { Box, CircularProgress, SxProps, Theme } from '@rhg/libs/material';
import NextImage from '@rhg/libs/next/image';
import { toDataURL as toQrCodeDataUrl } from 'qrcode';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

export const DEEPLINK_LINK_ACCESS_CODE_PREFIX = 'go';
export const DEEPLINK_LINK_LANDING = 'landing';

export interface QrCodeProps {
    url: string;
    sx?: SxProps<Theme>;
}
export function QrCode({ url, sx }: QrCodeProps) {
    const { formatMessage } = useIntl();
    const [imageDataUrl, setImageDataUrl] = useState('');

    useEffect(() => {
        const fetchQrCodeImageDataUrl = async () => {
            const dataUrl = await toQrCodeDataUrl(url);
            setImageDataUrl(dataUrl);
        };
        fetchQrCodeImageDataUrl().catch((err) =>
            logger.error('Failed to fetch QR code', {}, err)
        );
    }, [url]);

    return (
        <Box sx={sx}>
            {imageDataUrl && (
                <NextImage
                    width={0}
                    height={0}
                    sizes="100vw"
                    style={{
                        width: '100%',
                        height: '100%',
                    }}
                    src={imageDataUrl}
                    alt={formatMessage(commonMessages['common.qr_code'], {
                        url,
                    })}
                />
            )}
        </Box>
    );
}

export function AccessCodeQrCode({ sx }: Omit<QrCodeProps, 'url'>) {
    const { NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL: baseUrl } = useAppConfig();
    const { accessCode, isAccessCodeFromQuery, hasAccessCode } =
        useAccessCode();
    const { brandingPartnerConfig, isFetched: isBrandingPartnerConfigFetched } =
        usePublicBrandingPartnerConfig(accessCode);

    const isFetched = useMemo(
        () =>
            !isAccessCodeFromQuery || !hasAccessCode
                ? true
                : isBrandingPartnerConfigFetched,
        [hasAccessCode, isAccessCodeFromQuery, isBrandingPartnerConfigFetched]
    );

    const url = useMemo<string>(() => {
        if (!brandingPartnerConfig) {
            return `${baseUrl}/${DEEPLINK_LINK_LANDING}`;
        } else {
            return `${baseUrl}/${DEEPLINK_LINK_ACCESS_CODE_PREFIX}${accessCode}`;
        }
    }, [accessCode, baseUrl, brandingPartnerConfig]);

    return isFetched ? <QrCode sx={sx} url={url} /> : <CircularProgress />;
}
